/**
 * A deploy replaces every build artefact, and the names carry a content hash.
 * A tab that was open across it holds an index.html referring to files that no
 * longer exist, so the next lazy route fails with
 *
 *   Failed to fetch dynamically imported module …
 *   Loading chunk 42 failed
 *
 * and the page half-renders. The user did nothing wrong and has no way to know
 * a reload fixes it.
 *
 * The first attempt at this tried to make the edge answer 404 for missing
 * artefacts. That never worked — Cloudflare Pages' `_redirects` does not
 * support a 404 status, so the rules were discarded whole — and the file that
 * looked like protection sat in the repo for two days. This works instead
 * because it needs nothing from the host.
 */
export function isChunkLoadFailure(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;

  // Chrome, Firefox and Safari each word it differently, and the webpack-era
  // phrasing still appears from older tooling.
  return /Failed to fetch dynamically imported module/i.test(message)
      || /error loading dynamically imported module/i.test(message)
      || /Loading chunk .* failed/i.test(message)
      || /Importing a module script failed/i.test(message)
      // The one that reads least like a chunk problem: the browser asked for a
      // .js file and the host answered with index.html.
      || (/MIME type/i.test(message) && /text\/html/i.test(message));
}

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const e = error as { message?: unknown; rejection?: unknown };
    // Angular wraps an unhandled promise rejection, which is how a failed
    // dynamic import usually arrives.
    if (e.rejection) return messageOf(e.rejection);
    if (typeof e.message === 'string') return e.message;
  }
  return '';
}
